// +----------------------------------------------------------------------
// | AI 对话接口（Phase 9）
// |
// | CRUD 走统一的 axios 实例（自动带双 Header、自动解包、401 自动跳登录）；
// | 流式单独用 fetch —— axios 在浏览器端拿不到增量响应体，只能用 fetch 的
// | ReadableStream 一段段读。
// +----------------------------------------------------------------------
import request from '../lib/request';
import { getAuth } from '../lib/storage';
import { API_BASE } from '../lib/config';

/** 会话列表 */
export function listSessions(params = {}) {
  return request.get('/chats', { params });
}

/** 新建会话 */
export function createSession(title) {
  return request.post('/chats', title ? { title } : {});
}

/** 会话详情 + 消息（before_id 往前翻页） */
export function getSession(id, params = {}) {
  return request.get(`/chats/${id}`, { params });
}

/** 改标题 */
export function renameSession(id, title) {
  return request.put(`/chats/${id}`, { title });
}

/** 删会话 */
export function deleteSession(id) {
  return request.delete(`/chats/${id}`);
}

/** 清空全部会话 */
export function clearSessions() {
  return request.delete('/chats');
}

/** 发消息（非流式，兜底用） */
export function sendMessage(id, content) {
  return request.post(`/chats/${id}/messages`, { content });
}

/**
 * 发消息（流式主路径）
 *
 * 事件顺序：start → delta × N → done；出错则是 error。
 * 服务端最后一定会补一行 data: [DONE]，所以 onFinish 表示「这次流干净地结束了」。
 *
 * @param {number} id 会话 id
 * @param {string} content 用户消息
 * @param {{onStart?:Function,onDelta?:Function,onDone?:Function,onError?:Function,onFinish?:Function}} handlers
 * @param {AbortSignal} [signal] 离开页面时中断
 */
export async function streamMessage(id, content, handlers = {}, signal) {
  const auth = getAuth() || {};

  const response = await fetch(`${API_BASE}/chats/${id}/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      username: auth.username || '',
      token: auth.token || '',
    },
    body: JSON.stringify({ content }),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`流式请求失败（${response.status}）`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  const handleLine = (rawLine) => {
    const line = rawLine.trim();
    if (!line || line.startsWith(':')) return;

    if (!line.startsWith('data:')) return;

    const payload = line.slice(5).trim();
    if (payload === '[DONE]') {
      if (handlers.onFinish) handlers.onFinish();
      return;
    }

    let event;
    try {
      event = JSON.parse(payload);
    } catch (e) {
      return; // 半截 JSON（理论上不该有）直接忽略，不打断整条流
    }

    switch (event.type) {
      case 'start':
        if (handlers.onStart) handlers.onStart(event);
        break;
      case 'delta':
        if (handlers.onDelta) handlers.onDelta(event.text || '');
        break;
      case 'done':
        if (handlers.onDone) handlers.onDone(event);
        break;
      case 'error': {
        // code / quota 要透传出去：SSE 的 HTTP 状态一直是 200，
        // 前端只能靠 code 分辨原因（429 = 免费额度用完 → 弹付费引导，别给「重试」）
        if (handlers.onError) {
          const err = new Error(event.msg || '这次没答上来');
          err.code = event.code || 0;
          err.quota = event.quota || null;
          handlers.onError(err);
        }
        break;
      }
      default:
        break;
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

    let index = buffer.indexOf('\n\n');
    while (index !== -1) {
      handleLine(buffer.slice(0, index));
      buffer = buffer.slice(index + 2);
      index = buffer.indexOf('\n\n');
    }
  }

  // 收尾：把残留的最后一段也处理掉
  if (buffer.trim()) {
    buffer.split('\n').forEach(handleLine);
  }
}
